import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tabs, Tab, Box } from '@mui/material';
import EdTextfield from '@/components/forms/EdTextfield';
import { useForm, FormProvider } from 'react-hook-form';
import { RiImageAddLine, RiMore2Fill } from "react-icons/ri";
import { Cropper } from 'react-cropper';
import "cropperjs/dist/cropper.css";
import { Menu, MenuItem } from "@mui/material";
import {
  TEModal,
  TEModalDialog,
  TEModalContent,
  TEModalBody,
  TEModalFooter,
  TEModalHeader,
} from "tw-elements-react";
import { DefaultEditor } from 'react-simple-wysiwyg';
import '../CorsiCreatePage.css';
import AttoriDidatticiSection from './sections/AttoriDidatticiSection';
import DocumentsSection from './sections/DocumentsSection';
import CondizioniSection from './sections/CondizioniSection';
import MaterialeDidatticoSection from './sections/MaterialeDidatticoSection';
import DiscentiSection from './sections/DiscentiSection';
import { useSidebarStore } from '@/stores/sidebar.store';
import EdSelect from '@/components/forms/EdSelect';
import CourseSettingsModal from '../components/CourseSettingsModal';

const sections = [
  { id: 'dati-generali', label: 'Dati generali' },
  { id: 'attori-didattici', label: 'Attori didattici' },
  { id: 'discenti', label: 'Discenti' },
  { id: 'condizioni', label: 'Condizioni' },
  { id: 'materiale-didattico', label: 'Materiale didattico' },
  { id: 'documenti', label: 'Documenti' },
] as const;

// Opzioni per i select
const categoryOptions = [
  { label: 'Sicurezza', value: 'Sicurezza' },
  { label: 'Sanitario', value: 'Sanitario' },
  { label: 'Ambientale', value: 'Ambientale' },
  { label: 'Giuridico', value: 'Giuridico' },
  { label: 'Alimentare', value: 'Alimentare' },
  { label: 'Informatico', value: 'Informatico' },
  { label: 'Management', value: 'Management' },
];

const classOptions = [
  { label: 'Classe A', value: 'A' },
  { label: 'Classe B', value: 'B' },
  { label: 'Classe C', value: 'C' },
];

const templateOptions = [
  { label: 'Template Generico', value: 'template_generico' },
  { label: 'Template Sicurezza', value: 'template_sicurezza' },
  { label: 'Template Sanitario', value: 'template_sanitario' },
  { label: 'Template Ambientale', value: 'template_ambientale' },
  { label: 'Template Personalizzato', value: 'template_personalizzato' },
];

type SectionId = typeof sections[number]['id'];

interface ImageUploadState {
  file: File | null;
  preview: string;
  tempPreview: string;
}

export default function WebinarCreatePage(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { isOpen: sidebarOpen } = useSidebarStore();

  // Gestione modalità e dati passati
  const initialMode = location.state?.mode || 'create'; // 'create', 'edit', 'view'
  const [mode, setMode] = useState<'create' | 'edit' | 'view'>(initialMode);
  const courseData = location.state?.courseData || null;
  const returnTo = location.state?.returnTo as string | undefined;
  const isReadOnly = mode === 'view';

  const methods = useForm({
    mode: "onSubmit",
    defaultValues: {
      category: courseData?.categoria || "",
      class: courseData?.classe || '',
      duration: courseData?.durataOre || '',
      courseName: courseData?.nome || '',
      code: courseData?.codice || '',
      dicitura: '',
      status: 'wip',
      dicituraLegislativa: '',
      certificationValidity: '',
      certificationPrice: '',
      credits: courseData?.cfp || '',
      certificateTemplate: '',
      logo: '',
      attoreDidattico: 'ente'
    },
  });

  const { register, watch, setValue, formState: { errors } } = methods;

  const watchFields = watch();
  const [html, setHtml] = useState('my <b>HTML</b>');
  const [programma, setProgramma] = useState('my <b>HTML</b>');

  // Form change detection
  useEffect(() => {
    const subscription = watch((_, { type }) => {
      if (type === 'change') {
        setHasFormChanges(true);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  
  function onChange(e: any) {
    setHtml(e.target.value);
  }
  function onChangeProgramma(e: any) {
    setProgramma(e.target.value);
  }

  // Funzione per tornare alla griglia di provenienza
  const goBack = () => {
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate(-1);
    }
  };

  // Save and cancel handlers
  const handleSave = () => {
    // Add save logic here
    console.log('Saving form...', watchFields);
    setHasFormChanges(false);
    goBack();
  };

  const handleCancel = () => {
    // Reset form to default values
    methods.reset();
    setHtml('my <b>HTML</b>');
    setProgramma('my <b>HTML</b>');
    setHasFormChanges(false);
    goBack();
  };

  const handleSwitchToEdit = () => {
    setMode('edit');
  };
  // Stati per la gestione delle immagini
  const [showCropModal, setShowCropModal] = useState(false);
  const [activeUploadType, setActiveUploadType] = useState<"logo" | null>(null);
  const [logoState, setLogoState] = useState<ImageUploadState>({
    file: null,
    preview: "",
    tempPreview: "",
  });

  const cropperRef = useRef<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeMenu, setActiveMenu] = useState<"logo" | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Funzioni per la gestione delle immagini
  const getStateByType = (type: "logo"): [ImageUploadState, React.Dispatch<React.SetStateAction<ImageUploadState>>] => {
    switch (type) {
      case "logo":
        return [logoState, setLogoState];
    }
  };

  const handleFileSelect = (type: "logo") => async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const [, setState] = getStateByType(type);
      
      // Controlla se il file è un SVG
      if (file.type === 'image/svg+xml') {
        // Per SVG, mostra direttamente l'anteprima senza effettuare richieste
        const objectUrl = URL.createObjectURL(file);
        setValue(type, objectUrl);
        setState((prev) => ({
          ...prev,
          file,
          preview: objectUrl,
          tempPreview: objectUrl,
        }));
      } else {
        // Per altri formati immagine, procedi con il cropping
        setState((prev) => ({
          ...prev,
          file,
          preview: URL.createObjectURL(file),
          tempPreview: URL.createObjectURL(file),
        }));
        setActiveUploadType(type);
        setShowCropModal(true);
      }
    }
  };


  const createCroppedImage = async () => {
    if (!activeUploadType) return;

    const [state, setState] = getStateByType(activeUploadType);
    if (!state.file) return;

    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    const canvas = cropper.getCroppedCanvas();
    if (!canvas) return;

    canvas.toBlob(async (blob: Blob | null) => {
      if (blob) {
        const croppedFile = new File([blob], state.file?.name || "cropped.jpg", {
          type: "image/jpeg",
        });

        const objectUrl = URL.createObjectURL(croppedFile);
        if (activeUploadType) {
          setValue(activeUploadType, objectUrl);
        }

        setState((prev) => ({
          ...prev,
          file: croppedFile,
          preview: objectUrl,
          tempPreview: "",
        }));
      }
    }, "image/jpeg");

    setShowCropModal(false);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLDivElement>,
    type: "logo"
  ) => {
    setAnchorEl(event.currentTarget);
    setActiveMenu(type);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenu(null);
  };

  const handleChange = () => {
    if (activeMenu) {
      // Crea un input file nascosto e simula il click
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = (e) => {
        const target = e.target as HTMLInputElement;
        if (target.files?.length) {
          handleFileSelect(activeMenu)({
            target: { files: target.files },
          } as any);
        }
      };
      input.click();
      handleMenuClose();
    }
  };

  const handleDelete = async () => {
    if (activeMenu && watchFields[activeMenu]) {
      try {
        console.log(watchFields[activeMenu]);
        // Clear the corresponding field after successful deletion
        setValue(activeMenu, "");
      } catch (error) {
        console.error("Error deleting file:", error);
      }
    }
    handleMenuClose();
  };

  // Function to trigger file input click
  const triggerFileInput = (type: "logo") => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files?.length) {
        handleFileSelect(type)({
          target: { files: target.files },
        } as any);
      }
    };
    input.click();
  };

  // Refs per le sezioni
  const sectionRefs: Record<SectionId, React.RefObject<HTMLDivElement>> = {
    'dati-generali': useRef<HTMLDivElement>(null),
    'attori-didattici': useRef<HTMLDivElement>(null),
    'discenti': useRef<HTMLDivElement>(null),
    'condizioni': useRef<HTMLDivElement>(null),
    'materiale-didattico': useRef<HTMLDivElement>(null),
    'documenti': useRef<HTMLDivElement>(null),
  };

  // Funzione debounced per aggiornare la tab attiva
  const debouncedSetActiveTab = (index: number) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      setActiveTab(index);
    }, 100); // Debounce di 100ms
  };

  // Funzione per scrollare alla sezione
  const scrollToSection = (index: number) => {
    const sectionId = sections[index].id;
    const sectionRef = sectionRefs[sectionId];
    
    if (sectionRef?.current && scrollRef.current) {
      // Imposta subito la tab attiva per evitare rimbalzi
      setActiveTab(index);
      
      const elementRect = sectionRef.current.getBoundingClientRect();
      const containerRect = scrollRef.current.getBoundingClientRect();
      const headerOffset = 70; // Piccolo offset per non attaccare completamente al bordo
      
      const relativeTop = elementRect.top - containerRect.top + scrollRef.current.scrollTop - headerOffset;
      
      scrollRef.current.scrollTo({
        top: relativeTop,
        behavior: 'smooth',
      });
    }
  };

  // Setup Intersection Observer
  useEffect(() => {
    let observers: IntersectionObserver[] = [];

    const setupObservers = () => {
      observers = [];
      
      // Usa il viewport come root invece del container scrollabile
      const options = {
        root: null, // viewport
        rootMargin: '-20% 0px -60% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1]
      };

      sections.forEach((section, index) => {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.25) {
              debouncedSetActiveTab(index);
            }
          });
        }, options);

        const element = sectionRefs[section.id].current;
        if (element) {
          observer.observe(element);
          observers.push(observer);
        }
      });
    };

    // Fallback: listener di scroll manuale
    const handleScroll = () => {
      if (!scrollRef.current) return;
      
      const scrollContainer = scrollRef.current;
      const containerRect = scrollContainer.getBoundingClientRect();
      const containerTop = containerRect.top;
      const containerHeight = containerRect.height;
      
      let currentSection = 0;
      let maxVisibleRatio = 0;

      sections.forEach((section, index) => {
        const element = sectionRefs[section.id].current;
        if (!element) return;

        const elementRect = element.getBoundingClientRect();
        const elementTop = elementRect.top;
        const elementHeight = elementRect.height;
        const elementBottom = elementRect.bottom;

        // Calcola quanto è visibile della sezione
        const visibleTop = Math.max(elementTop, containerTop);
        const visibleBottom = Math.min(elementBottom, containerTop + containerHeight);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        const visibleRatio = visibleHeight / elementHeight;

        if (visibleRatio > maxVisibleRatio) {
          maxVisibleRatio = visibleRatio;
          currentSection = index;
        }
      });

      if (maxVisibleRatio > 0.3) { // Solo se almeno il 30% è visibile
        debouncedSetActiveTab(currentSection);
      }
    };

    // Attendi che tutti i refs siano disponibili
    const checkRefsAndSetup = () => {
      const allRefsExist = sections.every(
        section => sectionRefs[section.id].current !== null
      );

      if (allRefsExist && scrollRef.current) {
        setupObservers();
        scrollRef.current.addEventListener('scroll', handleScroll, { passive: true });
      } else {
        // Ritenta dopo un breve delay
        setTimeout(checkRefsAndSetup, 100);
      }
    };

    checkRefsAndSetup();

    // Cleanup function
    return () => {
      observers.forEach(observer => observer.disconnect());
      if (scrollRef.current) {
        scrollRef.current.removeEventListener('scroll', handleScroll);
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <FormProvider {...methods}>
      <>
      <div className="h-full overflow-y-auto text-black" ref={scrollRef}>
        <div className="flex flex-col">
          {/* Titolo che scrolla normalmente */}
          <div className="px-6 pt-6 pb-3 pb-0 bg-gray-50 flex justify-between items-center w-full">
            <h1 className="text-2xl font-bold mb-4 text-black">
              {mode === 'create' && 'Nuovo webinar'}
              {mode === 'edit' && 'Modifica webinar'}
              {mode === 'view' && 'Visualizza webinar'}
            </h1>
            <div className="flex items-center gap-2 w-[300px]">
              <div className="bg-white rounded">
                <EdSelect
                    id="status"
                    label="Stato"
                    options={[{ label: 'In programmazione', value: 'wip' }, { label: 'Pubblicato', value: 'published' }, { label: 'Finito', value: 'finished' }]}
                    register={register("status")}
                    value={watchFields.status}
                    error={errors.status && errors.status?.message?.toString()}
                     />
              </div>
                   <i 
                     className="ri-settings-4-fill cursor-pointer text-2xl text-gray-700 hover:text-[#B80F15] transition-colors" 
                     onClick={() => setShowSettingsModal(true)}
                   ></i>

            </div>
          </div>

          {/* Tab Navigation - solo queste sono sticky */}
          <div className="sticky top-0 z-20 bg-white">
            <Box sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              backgroundColor: '#F8F9F9'
            }}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => scrollToSection(newValue)}
                variant="scrollable"
                style={{ height: 50 }}
                scrollButtons="auto"
                sx={{
                  outline: 'none',
                  
                  border: 'none',
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#B80F15',
                    height: 3,
                  },
                  '& .MuiTab-root': {
                    color: '#6B7280',
                    fontWeight: 500,
                    padding: '10px 0px',
                    textTransform: 'uppercase',
                    fontSize: '0.875rem',
                    minHeight: 48,
                    '&.Mui-selected': {
                      color: '#B80F15',
                      fontWeight: 600,
                      outline: 'none',
                      boxShadow: 'none',
                      border: 'none',
                    },
                    '&:hover': {
                      color: '#B80F15',
                      backgroundColor: 'rgba(184, 15, 21, 0.04)',
                    },
                    '&:active':{
                      border: 'none',
                      outline: 'none',
                      boxShadow: 'none',
                    },
                    '&:focus':{
                      border: 'none',
                      boxShadow: 'none',
                      outline: 'none',
                    }
                  },
                }}
              >
                {sections.map((section, index) => (
                  <Tab
                  sx={{ minWidth: "fit-content", flex: 1 }}
                    key={section.id}
                    label={section.label}
                    id={`tab-${index}`}
                    aria-controls={`tabpanel-${index}`}
                  />
                ))}
              </Tabs>
            </Box>
          </div>

          {/* Content Sections */}
          <div className="flex flex-col gap-4 px-6 pt-6 box-border mb-[100px]">
              <div className="flex flex-col gap-6 w-full  bg-white" id="dati-generali" ref={sectionRefs['dati-generali']}>
                <h2 className="text-[14px] text-[#4F4F4F] font-bold mb-2">Dati webinar</h2>
                
                {/* Prima riga - 3 campi */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {isReadOnly ? (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Categoria</div>
                      <div className="text-gray-900">{watchFields.category || '--'}</div>
                    </div>
                  ) : (
                    <EdSelect
                      label="Categoria*"
                      id="category"
                      options={categoryOptions}
                      placeholder="Seleziona categoria"
                      register={register("category", {
                        required: "Campo obbligatorio",
                      })}
                      error={errors.category && errors.category?.message?.toString()}
                      value={watchFields.category}
                    />
                  )}
                  
                  {isReadOnly ? (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Classe</div>
                      <div className="text-gray-900">{watchFields.class || '--'}</div>
                    </div>
                  ) : (
                    <EdSelect
                      label="Classe*"
                      id="class"
                      options={classOptions}
                      placeholder="Seleziona classe"
                      register={register("class", {
                        required: "Campo obbligatorio",
                      })}
                      error={errors.class && errors.class?.message?.toString()}
                      value={watchFields.class}
                    />
                  )}
                  
                  {isReadOnly ? (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Durata corso in ore</div>
                      <div className="text-gray-900">{watchFields.duration || '--'}</div>
                    </div>
                  ) : (
                    <EdTextfield
                      label="Durata corso in ore"
                      type="number"
                      id="duration"
                      placeholder="Inserisci durata"
                      register={register("duration")}
                      error={errors.duration && errors.duration?.message?.toString()}
                    />
                  )}
                </div>

                {/* Seconda riga - Denominazione corso e Codice */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {isReadOnly ? (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Denominazione corso</div>
                      <div className="text-gray-900">{watchFields.courseName || '--'}</div>
                    </div>
                  ) : (
                    <EdTextfield
                      label="Denominazione corso*"
                      type="text"
                      id="courseName"
                      placeholder="Inserisci denominazione corso"
                      register={register("courseName", {
                        required: "Campo obbligatorio",
                      })}
                      error={errors.courseName && errors.courseName?.message?.toString()}
                    />
                  )}
                  
                  {isReadOnly ? (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Codice</div>
                      <div className="text-gray-900">{watchFields.code || '--'}</div>
                    </div>
                  ) : (
                    <EdTextfield
                      label="Codice"
                      type="text"
                      id="code"
                      placeholder="Inserisci codice"
                      register={register("code")}
                      error={errors.code && errors.code?.message?.toString()}
                    />
                  )}
                </div>

                {/* Terza riga - 3 campi: Dicitura corso, Dicitura legislativa, Validità attestato */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {isReadOnly ? (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Dicitura corso</div>
                      <div className="text-gray-900">{watchFields.dicitura || '--'}</div>
                    </div>
                  ) : (
                    <EdTextfield
                      label="Dicitura corso*"
                      type="text"
                      id="dicitura"
                      placeholder="Inserisci dicitura corso"
                      register={register("dicitura", {
                        required: "Campo obbligatorio",
                      })}
                      error={errors.dicitura && errors.dicitura?.message?.toString()}
                    />
                  )}
                  
                  {isReadOnly ? (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Dicitura legislativa</div>
                      <div className="text-gray-900">{watchFields.dicituraLegislativa || '--'}</div>
                    </div>
                  ) : (
                    <EdTextfield
                      label="Dicitura legislativa*"
                      type="text"
                      id="dicituraLegislativa"
                      placeholder="Inserisci dicitura legislativa"
                      register={register("dicituraLegislativa", {
                        required: "Campo obbligatorio",
                      })}
                      error={errors.dicituraLegislativa && errors.dicituraLegislativa?.message?.toString()}
                    />
                  )}
                  
                  {isReadOnly ? (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Validità attestato (in mesi)</div>
                      <div className="text-gray-900">{watchFields.certificationValidity || '--'}</div>
                    </div>
                  ) : (
                    <EdTextfield
                      label="Validità attestato (in mesi)"
                      type="number"
                      id="certificationValidity"
                      placeholder="Inserisci validità"
                      register={register("certificationValidity")}
                      error={errors.certificationValidity && errors.certificationValidity?.message?.toString()}
                    />
                  )}
                </div>

                {/* Quarta riga - 4 campi: Prezzi, crediti e template */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {isReadOnly ? (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Prezzo attestato (iva esclusa)</div>
                      <div className="text-gray-900">{watchFields.certificationPrice || '--'}</div>
                    </div>
                  ) : (
                    <EdTextfield
                      label="Prezzo attestato (iva esclusa)*"
                      type="number"
                      id="certificationPrice"
                      placeholder="Prezzo attestato (iva esclusa)*"
                      register={register("certificationPrice", {
                        required: "Campo obbligatorio",
                      })}
                      error={errors.certificationPrice && errors.certificationPrice?.message?.toString()}
                    />
                  )}
                  
                  <div className='flex flex-col  justify-start'>
                      <div className="text-sm font-medium text-gray-700 mb-0">Prezzo attestato (iva inclusa)</div>
                      <div className="text-gray-900 text-sm">
                        {watchFields.certificationPrice 
                          ? `€ ${(parseFloat(watchFields.certificationPrice) * 1.22).toFixed(2)}`
                          : '--'
                        }
                      </div>
                    </div>
                  
                  {isReadOnly ? (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Crediti formativi acquisiti</div>
                      <div className="text-gray-900">{watchFields.credits || '--'}</div>
                    </div>
                  ) : (
                    <EdTextfield
                      label="Crediti formativi acquisiti"
                      type="number"
                      id="credits"
                      placeholder="Inserisci crediti"
                      register={register("credits")}
                      error={errors.credits && errors.credits?.message?.toString()}
                    />
                  )}
                  
                  {isReadOnly ? (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Template attestato generico</div>
                      <div className="text-gray-900">{watchFields.certificateTemplate || '--'}</div>
                    </div>
                  ) : (
                    <EdSelect
                      label="Template attestato generico*"
                      id="certificateTemplate"
                      options={templateOptions}
                      placeholder="Seleziona template"
                      register={register("certificateTemplate", {
                        required: "Campo obbligatorio",
                      })}
                      error={errors.certificateTemplate && errors.certificateTemplate?.message?.toString()}
                      value={watchFields.certificateTemplate}
                    />
                  )}
                </div>

            
                {/* Sezione upload immagine */}
                <div className="flex flex-col gap-2">
                     {!isReadOnly && <h2 className="text-[14px] text-[#4F4F4F] font-bold mb-2">Seleziona immagine</h2>}
                  {isReadOnly ? (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Immagine corso</div>
                      {watchFields.logo ? (
                        <div className="w-36 h-36 border border-gray-300 rounded flex items-center justify-center bg-white">
                          <img
                            src={watchFields.logo}
                            alt="Logo preview"
                            className="object-contain w-full h-full rounded"
                          />
                        </div>
                      ) : (
                        <div className="text-gray-900">--</div>
                      )}
                    </div>
                  ) : (
                    <span 
                      className="w-36 h-36 border border-dashed border-gray-400 rounded flex flex-col items-center justify-center transition-colors bg-[#F8F9F9] cursor-pointer hover:border-[#B80F15]"
                      onClick={() => triggerFileInput("logo")}
                    >
                      {watchFields.logo &&
                      (activeUploadType !== "logo" || !showCropModal) ? (
                        <div className="relative w-full h-full">
                          <div
                            className="py-1.5 px-0.5 rounded-full bg-red-700 absolute top-2 right-2 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuOpen(e, "logo");
                            }}
                          >
                            <RiMore2Fill className="text-white" />
                          </div>
                          <img
                            src={watchFields.logo}
                            alt="Logo preview"
                            className="object-contain w-full h-full"
                          />
                        </div>
                      ) : (
                        <>
                          <RiImageAddLine className="text-[#B80F15] size-6" />
                          <span className="mt-2 text-xs font-semibold text-center text-[#B80F15]">
                            Clicca qui per
                            <br />
                            aggiungere foto
                          </span>
                        </>
                      )}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                <h2 className="text-sm font-medium text-gray-700 mb-3">Descrizione</h2>
                {isReadOnly ? (
                  <div>
                    <div 
                      className="text-gray-900 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: html || '<p>--</p>' }}
                      />
                  </div>
                ) : (
                  <DefaultEditor 
                  value={html} 
                  onChange={onChange} 
                  />
                )}
                </div>
                <div className="flex flex-col gap-1">

                <h2 className="text-sm font-medium text-gray-700 mb-3">Programma</h2>
                {isReadOnly ? (
                  <div>
                    <div 
                      className="text-gray-900 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: programma || '<p>--</p>' }}
                      />
                  </div>
                ) : (
                  <DefaultEditor 
                  value={programma} 
                  onChange={onChangeProgramma} 
                  />
                )}
                </div>

              </div>
            
            <div className="border-t border-gray-300 my-10"></div>
            
            <div className="flex flex-col gap-4 w-full  " id="attori-didattici" ref={sectionRefs['attori-didattici']}>
              <AttoriDidatticiSection isReadOnly={isReadOnly} />
            </div>
            
            <div className="border-t border-gray-300 my-10"></div>
            
            <div className="flex flex-col gap-4 w-full " id="discenti" ref={sectionRefs['discenti']}>
              <DiscentiSection isReadOnly={isReadOnly} />
            </div>
            
            <div className="border-t border-gray-300 my-10"></div>
            
            <div className="flex flex-col gap-4 w-full " id="condizioni" ref={sectionRefs['condizioni']}>
              <CondizioniSection isReadOnly={isReadOnly} />
            </div>
            
            <div className="border-t border-gray-300 my-10"></div>
            
            <div className="flex flex-col gap-4 w-full" id="materiale-didattico" ref={sectionRefs['materiale-didattico']}>
              <MaterialeDidatticoSection isReadOnly={isReadOnly} />
            </div>
            
            <div className="border-t border-gray-300 mb-10 mt-5"></div>
            
            <div className="flex flex-col gap-4 w-full  mb-[30px]" id="documenti" ref={sectionRefs['documenti']}>
              <DocumentsSection isReadOnly={isReadOnly} />
            </div>
          </div>
        </div>
      </div>

      {/* Modal per il Crop */}
      <TEModal show={showCropModal} setShow={setShowCropModal}>
        <TEModalDialog size="lg">
          <TEModalContent>
            <TEModalHeader>
              <h5 className="text-xl font-medium leading-normal text-neutral-800">
                Ritaglia immagine
              </h5>
            </TEModalHeader>
            <TEModalBody>
              <div className="relative h-[400px]">
                {activeUploadType && (
                  <Cropper
                    ref={cropperRef}
                    src={getStateByType(activeUploadType)[0].tempPreview}
                    style={{ height: 400, width: "100%" }}
                    guides={true}
                    preview=".img-preview"
                    viewMode={1}
                    dragMode="move"
                    cropBoxMovable={true}
                    cropBoxResizable={true}
                    toggleDragModeOnDblclick={false}
                  />
                )}
              </div>
            </TEModalBody>
            <TEModalFooter className="gap-4">
              <button
                className="rounded bg-red-700 px-6 py-2.5 text-xs font-medium uppercase text-white"
                onClick={createCroppedImage}
              >
                Conferma
              </button>
              <button
                className="rounded bg-gray-200 px-6 py-2.5 text-xs font-medium uppercase text-gray-700"
                onClick={() => setShowCropModal(false)}
              >
                Annulla
              </button>
            </TEModalFooter>
          </TEModalContent>
        </TEModalDialog>
      </TEModal>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleChange}>Cambia</MenuItem>
        <MenuItem onClick={handleDelete}>Elimina</MenuItem>
      </Menu>

      {/* Fixed Bottom Panel with Save/Cancel Buttons */}
      <div className={`fixed bottom-0 ${sidebarOpen ? 'left-64' : 'left-0'} right-0 z-50 transition-all duration-300 ease-in-out `}>
        <div className="bg-white border-t border-gray-200 shadow-lg px-6 py-4">
          <div className="flex justify-start gap-4">
            {isReadOnly ? (
              <>
                <button
                  onClick={handleSwitchToEdit}
                  className="px-6 py-2 text-sm font-medium text-white bg-[#B80F15] border border-transparent rounded-[4px] hover:bg-[#9A0C11] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#B80F15] transition-colors"
                >
                  Passa in modifica
                </button>
                <button
                  onClick={handleCancel}
                  className="px-6 py-2 text-sm font-medium text-[#285192] bg-[#E3EBF7]  rounded-[4px] hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                >
                  Annulla
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  className="px-6 py-2 text-sm font-medium text-white bg-[#B80F15] border border-transparent rounded-[4px] hover:bg-[#9A0C11] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#B80F15] transition-colors"
                >
                  Salva
                </button>
                <button
                  onClick={handleCancel}
                  className="px-6 py-2 text-sm font-medium text-[#285192] bg-[#E3EBF7]  rounded-[4px] hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                >
                  Annulla
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      <CourseSettingsModal 
        show={showSettingsModal} 
        onClose={() => setShowSettingsModal(false)} 
      />
      </>
    </FormProvider>
  );
}
